// File: client/src/variants/randomizer/useRandomizerRuntime.js
//
// Randomizer Chess runtime. Every 3 full turns a random event fires (board rotation,
// teleport, promotion, swap, gravity, meteor, or a knight-only turn). The NEXT event
// is previewed so players can plan. Deterministic via a per-game seed (reproducible,
// testable, resumable). Runs entirely through the variant contract — no host/engine
// changes (board events mutate the committed board in afterMove; the knight-only
// event is applied via transformLegalMoves).

import React, { useRef, useState, useCallback } from 'react';
import { Dices, Eye } from 'lucide-react';
import { createPRNG } from '../../platform/prng';
import { BOARD_MODIFIERS, knightOnly, pickModifier, modifierById } from '../../platform/modifiers';

export const EVENT_EVERY = 3; // full turns between events

// Randomizer's event pool: all board modifiers + Knight Fever (the one rule event
// this variant supports). Other rule modifiers (Frozen, Diagonal Pawns) are left to
// Chess Mayhem, which handles every rule type.
const RANDOMIZER_EVENTS = [...BOARD_MODIFIERS, knightOnly];
const pickEvent = (rng) => pickModifier(rng, RANDOMIZER_EVENTS);
const eventById = (id) => modifierById(id, RANDOMIZER_EVENTS);

const KNIGHT_OFFSETS = [
  [-2, -1], [-2, 1], [-1, -2], [-1, 2], [1, -2], [1, 2], [2, -1], [2, 1],
];

export function useRandomizerRuntime({ resumePayload }) {
  const seedRef = useRef(
    resumePayload && resumePayload.seed != null
      ? resumePayload.seed
      : Math.floor(Math.random() * 1e9)
  );
  // A single PRNG advanced across the game so the sequence is reproducible from seed.
  const rngRef = useRef(createPRNG(seedRef.current));
  // Replay the PRNG to the resumed position so the next event matches.
  const moveCountRef = useRef(resumePayload ? resumePayload.moveCount || 0 : 0);
  const nextEventRef = useRef(
    resumePayload && resumePayload.nextEventId
      ? eventById(resumePayload.nextEventId)
      : pickEvent(rngRef.current)
  );
  const knightOnlyForRef = useRef(null); // color whose next turn is knight-only

  const [display, setDisplay] = useState({
    next: nextEventRef.current,
    movesToEvent: EVENT_EVERY * 2 - (moveCountRef.current % (EVENT_EVERY * 2)),
    lastMessage: null,
  });

  const moveMiddleware = {
    transformLegalMoves(gameState, from, engineMoves) {
      // Knight-only event: replace this piece's moves with legal knight leaps.
      const activeColor = gameState.turn;
      if (knightOnlyForRef.current !== activeColor) return engineMoves;
      const board = gameState.board;
      const piece = board[from.row][from.col];
      if (!piece) return engineMoves;
      const isWhite = piece === piece.toUpperCase();
      const out = [];
      for (const [dr, dc] of KNIGHT_OFFSETS) {
        const r = from.row + dr;
        const c = from.col + dc;
        if (r < 0 || r > 7 || c < 0 || c > 7) continue;
        const t = board[r][c];
        if (!t || t === '' || (t === t.toUpperCase()) !== isWhite) {
          out.push({ row: r, col: c, type: t ? 'capture' : 'normal' });
        }
      }
      return out;
    },

    afterMove(newState) {
      moveCountRef.current += 1;
      const period = EVENT_EVERY * 2; // half-moves between events

      // A knight-only event flag is spent once its owner (the mover) has moved.
      if (knightOnlyForRef.current === newState.turn) {
        knightOnlyForRef.current = null;
      }

      let message = null;
      // Fire an event exactly when we complete a multiple of EVENT_EVERY full turns.
      if (moveCountRef.current % period === 0) {
        const event = nextEventRef.current || pickEvent(rngRef.current);
        if (event.rule === 'knight_only') {
          // Applies to the side about to move next.
          knightOnlyForRef.current = newState.turn === 'white' ? 'black' : 'white';
          message = event.apply();
        } else {
          message = event.apply(newState.board, rngRef.current, { newState });
        }
        nextEventRef.current = pickEvent(rngRef.current); // next preview
      }

      const rem = moveCountRef.current % period;
      setDisplay(prev => ({
        next: nextEventRef.current,
        movesToEvent: rem === 0 ? period : period - rem,
        lastMessage: message || prev.lastMessage,
      }));

      return { turnEnded: true };
    },
  };

  const getPersistencePayload = useCallback(() => ({
    seed: seedRef.current,
    moveCount: moveCountRef.current,
    nextEventId: nextEventRef.current ? nextEventRef.current.id : null,
  }), []);

  const renderOverlay = (slot) => {
    if (slot !== 'top') return null;
    const next = display.next;
    return (
      <div className="flex flex-col items-center gap-1 my-2">
        <div className="flex flex-wrap gap-2 items-center justify-center">
          <span className="flex items-center gap-1 px-3 py-1 text-xs font-semibold rounded-full border bg-purple-700/40 border-purple-500/50 text-purple-200">
            <Dices size={14} /> Event in {display.movesToEvent} move{display.movesToEvent === 1 ? '' : 's'}
          </span>
          {next && (
            <span className="flex items-center gap-1 px-3 py-1 text-xs font-semibold rounded-full border bg-slate-700 border-slate-600 text-slate-200">
              <Eye size={14} /> Next: {next.icon} {next.name}
            </span>
          )}
        </div>
        {display.lastMessage && (
          <span className="text-[11px] text-amber-300">⚡ {display.lastMessage}</span>
        )}
      </div>
    );
  };

  return { moveMiddleware, getPersistencePayload, renderOverlay };
}
